// src/authService.js
import { signInWithCustomToken, signOut } from "firebase/auth";
import Cookies from "js-cookie";
import { auth } from "./firebase";
import { bootstrapAuthenticatedSession, waitForFirebaseTokenSync } from "./authBootstrap";

const SSO_SESSION_KEY = "panelSsoSession";
const SSO_TOKEN_KEY = "empresasJWT";

function clearLegacySsoStorageKeys() {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("partner-sso:") || key.startsWith("partner-sso")) {
      localStorage.removeItem(key);
    }
  });
}

export function clearSsoSession() {
  clearLegacySsoStorageKeys();

  const storedSsoToken = localStorage.getItem(SSO_TOKEN_KEY) || localStorage.getItem("ssoToken");

  if (storedSsoToken) {
    ["token", "authToken", "iOS_token"].forEach((key) => {
      if (localStorage.getItem(key) === storedSsoToken) {
        localStorage.removeItem(key);
      }
    });
  }

  localStorage.removeItem(SSO_SESSION_KEY);
  localStorage.removeItem(SSO_TOKEN_KEY);
  localStorage.removeItem("ssoToken");
}

export function getStoredSsoSession() {
  try {
    const rawSession = localStorage.getItem(SSO_SESSION_KEY);
    if (!rawSession) return null;

    const session = JSON.parse(rawSession);
    if ((!session?.ssoToken && !session?.accessToken) || !session?.empresaId) {
      clearSsoSession();
      return null;
    }

    if (session.expiresAt && new Date(session.expiresAt).getTime() <= Date.now()) {
      clearSsoSession();
      return null;
    }

    return session;
  } catch (error) {
    console.error("❌ [AuthService] Error al leer sesión SSO:", error);
    clearSsoSession();
    return null;
  }
}

export function getSsoToken() {
  const session = getStoredSsoSession();
  return session?.accessToken || session?.ssoToken || null;
}

export function getSsoUser() {
  const session = getStoredSsoSession();
  if (!session) return null;

  return {
    uid: `sso-${session.empresaId}`,
    isSso: true,
    empresaId: session.empresaId,
    partnerId: session.partnerId,
    email: session.empresa?.email || null,
    displayName: session.empresa?.nombre || "Usuario SSO",
  };
}

function normalizeSsoValidationResponse(validationData) {
  const nested = validationData?.data && typeof validationData.data === "object" ? validationData.data : {};

  return {
    valid: validationData?.valid ?? nested?.valid,
    auth: validationData?.auth || nested?.auth,
    empresaId: validationData?.empresaId || nested?.empresaId,
    empresa: validationData?.empresa || nested?.empresa,
    partnerId: validationData?.partnerId || nested?.partnerId,
  };
}

/**
 * Completa el login SSO: limpia tokens viejos, intercambia customToken por ID token de Firebase
 * y ejecuta el mismo bootstrap que el login normal.
 */
export async function completeSsoLogin(validationData) {
  const payload = normalizeSsoValidationResponse(validationData);
  clearSsoSession();

  if (!payload.valid) {
    throw new Error("La respuesta de SSO no es válida");
  }

  const customToken = payload.auth?.customToken;
  if (!customToken || typeof customToken !== "string" || customToken.trim().length === 0) {
    throw new Error("La respuesta de SSO no incluye un customToken de Firebase válido");
  }

  localStorage.removeItem("logout_intentional");

  if (auth.currentUser) {
    await signOut(auth);
  }

  const idToken = await signInWithFirebaseToken(customToken);
  const finalToken = await waitForFirebaseTokenSync(idToken);
  const { userData, empresaData } = await bootstrapAuthenticatedSession();

  return {
    userData,
    empresaData,
    idToken: finalToken,
    partnerId: payload.partnerId,
    empresaId: payload.empresaId || payload.empresa?.id || userData.empresaId,
  };
}

/**
 * Flujo completo de autenticación:
 * 1. Login: el endpoint /login devuelve un customToken de Firebase
 * 2. Intercambio en el cliente: usa el customToken con el SDK de Firebase para obtener un ID token
 * 3. Request: envía el ID token en el header Authorization
 */

/**
 * Paso 2: Intercambia el customToken por ID token usando Firebase SDK
 * Después de recibir el customToken del endpoint /login, usa signInWithCustomToken
 * para autenticar con Firebase y obtener el ID token real.
 * 
 * @param {string} customToken - El custom token recibido del endpoint /login
 * @returns {Promise<string>} - El ID token de Firebase para usar en requests autenticadas
 */
export async function signInWithFirebaseToken(customToken) {
  try {
    console.log("🔍 [AuthService] Iniciando intercambio de customToken...");
    
    // Validar que el token existe y no está vacío
    if (!customToken || typeof customToken !== "string" || customToken.trim().length === 0) {
      throw new Error("El customToken está vacío o no es válido");
    }
    
    // Validar formato básico del token (debe tener exactamente 3 partes separadas por puntos)
    const tokenParts = customToken.split(".");
    if (tokenParts.length !== 3) {
      console.error("❌ [AuthService] El token no tiene el formato correcto de JWT");
      console.error("❌ [AuthService] Número de partes:", tokenParts.length);
      console.error("❌ [AuthService] Token recibido (primeros 100 caracteres):", customToken.substring(0, 100));
      throw new Error("El customToken no tiene el formato correcto de Firebase custom token");
    }

    // Intentar decodificar el payload del JWT para verificar que tenga el uid
    try {
      const payload = JSON.parse(atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')));
      console.log("🔍 [AuthService] Payload del token decodificado:", {
        uid: payload.uid,
        iss: payload.iss,
        aud: payload.aud,
        exp: payload.exp,
        iat: payload.iat,
        hasUid: !!payload.uid
      });
      
      // Verificar que el token tenga un uid (requerido para custom tokens de Firebase)
      if (!payload.uid) {
        console.error("❌ [AuthService] El token no contiene un 'uid' en el payload");
        console.error("❌ [AuthService] Payload completo:", payload);
        throw new Error("El custom token no contiene un identificador de usuario (uid). El backend debe generar el token con Firebase Admin SDK usando admin.auth().createCustomToken(uid)");
      }

      // Verificar que el issuer sea correcto (debe ser el projectId de Firebase)
      const expectedIss = `https://securetoken.google.com/${auth.app.options.projectId}`;
      if (payload.iss !== expectedIss) {
        console.warn("⚠️ [AuthService] El issuer del token no coincide con el esperado");
        console.warn("⚠️ [AuthService] Issuer recibido:", payload.iss);
        console.warn("⚠️ [AuthService] Issuer esperado:", expectedIss);
      }
    } catch (decodeError) {
      console.error("❌ [AuthService] No se pudo decodificar el payload del token:", decodeError);
      console.error("❌ [AuthService] Esto podría indicar que el token no es un JWT válido");
      // No lanzar error aquí, dejar que Firebase intente validarlo
    }
    
    console.log("🔍 [AuthService] Intentando autenticar con Firebase usando el customToken...");
    
    // Paso importante: usar el customToken devuelto por el backend
    // NOTA: Si el backend devuelve un JWT normal (no un custom token de Firebase),
    // este paso fallará. El backend debe usar Firebase Admin SDK para generar
    // el custom token: admin.auth().createCustomToken(uid)
    const userCredential = await signInWithCustomToken(auth, customToken);
    
    console.log("✅ [AuthService] Autenticación con Firebase exitosa");
    console.log("🔍 [AuthService] Usuario autenticado:", userCredential.user.uid);
    
    // Luego obtener el ID token real de Firebase
    const idToken = await userCredential.user.getIdToken();
    
    if (!idToken) {
      throw new Error("No se pudo obtener el ID token de Firebase después de la autenticación");
    }
    
    console.log("✅ [AuthService] ID token obtenido exitosamente");
    
    clearSsoSession();

    // Guardar el ID token en localStorage
    localStorage.setItem("token", idToken);
    localStorage.setItem("authToken", idToken);
    localStorage.setItem("iOS_token", idToken);
    
    return idToken;
  } catch (error) {
    console.error("❌ [AuthService] Error al intercambiar customToken:", error);
    console.error("❌ [AuthService] Error code:", error.code);
    console.error("❌ [AuthService] Error message:", error.message);
    console.error("❌ [AuthService] Stack:", error.stack);
    
    // Log adicional del token para debugging (sin exponer el token completo)
    if (customToken && typeof customToken === "string") {
      const tokenPreview = customToken.length > 50 
        ? `${customToken.substring(0, 50)}... (${customToken.length} caracteres)`
        : customToken;
      console.error("❌ [AuthService] Token que causó el error:", tokenPreview);
    }
    
    // Mensaje más descriptivo para el usuario
    if (error.code === "auth/missing-identifier") {
      console.error("❌ [AuthService] ERROR ESPECÍFICO: auth/missing-identifier");
      console.error("❌ [AuthService] Este error generalmente ocurre cuando:");
      console.error("   1. El custom token no fue generado con Firebase Admin SDK");
      console.error("   2. El token no contiene un 'uid' válido en el payload");
      console.error("   3. El token está mal formado o corrupto");
      console.error("❌ [AuthService] El backend debe usar: admin.auth().createCustomToken(uid)");
      
      const detailedError = new Error(
        "El backend no está devolviendo un custom token de Firebase válido. " +
        "El backend debe usar Firebase Admin SDK para generar el token: " +
        "admin.auth().createCustomToken(uid). " +
        "Verifica que el backend esté configurado correctamente en producción."
      );
      detailedError.originalError = error;
      throw detailedError;
    }
    
    throw error;
  }
}

/**
 * Paso 3: Obtiene el ID token actual para usar en requests
 * Firebase maneja automáticamente la renovación de tokens, así que normalmente
 * no necesitamos forzar el refresh (a menos que sepamos que el token expiró)
 * 
 * @param {boolean} forceRefresh - Si es true, fuerza la renovación del token
 * @returns {Promise<string|null>} - El ID token de Firebase o null si no hay usuario autenticado
 */
export async function getFirebaseToken(forceRefresh = false) {
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const idToken = await currentUser.getIdToken(forceRefresh);

      if (!idToken) {
        console.error("❌ [AuthService] No se pudo obtener el token");
        return null;
      }

      localStorage.setItem("token", idToken);
      localStorage.setItem("authToken", idToken);
      localStorage.setItem("iOS_token", idToken);

      return idToken;
    }

    return null;
  } catch (error) {
    console.error("❌ [AuthService] Error al obtener token:", error);
    // Si el error es de autenticación, limpiar el estado
    if (error.code === 'auth/user-token-expired' || error.code === 'auth/user-disabled') {
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
      localStorage.removeItem("iOS_token");
    }
    return null;
  }
}

export async function logout() {
  // Marcar que se está haciendo logout intencionalmente
  // Esto previene que el Login redirija automáticamente
  localStorage.setItem("logout_intentional", "true");
  
  await signOut(auth);
  
  clearSsoSession();
  localStorage.removeItem("token");
  localStorage.removeItem("authToken");
  localStorage.removeItem("iOS_token");
  localStorage.removeItem("userId");
  localStorage.removeItem("firebaseUid");
  localStorage.removeItem("empresaId");
  localStorage.removeItem("empresaData");
  Cookies.remove("userId");
  Cookies.remove("empresaId");
  Cookies.remove("empresaData");
}


